/**
 * DS2026 Field primitives
 *
 * In-repo recreation of the DS2026 "Input field" + "Text area" Figma specs
 * (First Bite Design System, nodes 1536:18108 / 7148:6098), built on our own
 * design tokens + react-hook-form — no external form library.
 *
 * Composition mirrors the tailgrids `Field` API the design references:
 *
 *   <FieldGroup>
 *     <DsTextField control={control} name="first_name" label="First name" />
 *     <DsTextAreaField control={control} name="notes" label="Notes" optional />
 *   </FieldGroup>
 *
 * Tokens (Figma → repo):
 *   - default border  #d1d5db  → border-neutral-300
 *   - placeholder     #9ca3af  → placeholder:text-neutral-400
 *   - label           #374151  → text-neutral-700, 14px / medium / -0.2px
 *   - focus (brand)   #087443 border + 4px #08744333 ring
 *                              → border-brand-active + ring-4 ring-brand-active/20
 *                                (brand-active @ 20% == #08744333, exact)
 *   - error / success use our AA-tuned status tokens rather than the raw Figma
 *     red-500/green-500 (#ef4444/#22c55e), which fail AA as message text.
 *   - resting elevation: shadow-sm (composes with the focus ring; an arbitrary
 *     box-shadow would clobber the ring).
 *
 * Accessibility: label↔input via htmlFor/id, description + error linked through
 * aria-describedby, aria-invalid toggled on error.
 */
import { cn } from 'components/UI/cn'
// PropTypes is required on the forwardRef primitives below: the repo's
// react/prop-types lint rule errors without it (same as FormUtilsV2/Input.tsx).
import PropTypes from 'prop-types'
import * as React from 'react'
import {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  useController,
} from 'react-hook-form'
import { colors } from '../../styles/tokens'

// ---------------------------------------------------------------------------
// react-select styling — DS2026 control look for the select-based fields
// (CreatableSelect etc. accept a `styles` prop). Keeps the dropdowns visually
// in step with the DsInput controls: same border, radius, height, focus ring.
// ---------------------------------------------------------------------------

/**
 * react-select `styles` object that makes a dropdown match the DS2026 text
 * input: same 36px height, border, radius, shadow, brand focus ring, placeholder
 * color, plus a DS2026 caret, indicator separator, and clear (X) affordance.
 * Pair with `isClearable` to surface the X. Spreadable into other react-select
 * style objects (the tag picker reuses the control/indicator keys).
 */
export const ds2026SelectStyles = {
  control: (base: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...base,
    minHeight: 36,
    borderRadius: 8,
    backgroundColor: colors.surface,
    borderColor: state.isFocused
      ? colors['brand-active']
      : colors['neutral-300'],
    boxShadow: state.isFocused
      ? `0 0 0 4px ${colors['brand-focus']}` // brand-active @ 30% ring
      : 'none', // no resting shadow — only on hover (below)
    '&:hover': {
      borderColor: state.isFocused
        ? colors['brand-active']
        : colors['neutral-400'],
      boxShadow: state.isFocused
        ? `0 0 0 4px ${colors['brand-focus']}`
        : '0 1px 2px 0 rgba(16,24,40,0.05)', // shadow-sm on hover
    },
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    padding: '2px 12px',
    flexWrap: 'nowrap' as const, // chips stay on one line; overflow collapses to "+N"
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: 12,
    color: colors.fg,
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    fontSize: 12,
    color: colors.fg,
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    fontSize: 12,
    color: colors['neutral-400'],
  }),
  // Caret — DS2026 neutral chevron.
  dropdownIndicator: (
    base: Record<string, unknown>,
    state: { isFocused: boolean }
  ) => ({
    ...base,
    padding: '0 10px',
    color: state.isFocused ? colors['neutral-600'] : colors['neutral-500'],
    '&:hover': { color: colors['neutral-700'] },
  }),
  // Clear (X) — shown when `isClearable` and a value is present. Extra right
  // padding sets it off from the divider; rounded hover bg like the close icon.
  clearIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: 4,
    marginRight: 4,
    borderRadius: 6,
    color: colors['neutral-400'],
    cursor: 'pointer',
    transition: 'background-color 120ms, color 120ms',
    '&:hover': {
      color: colors['neutral-600'],
      backgroundColor: colors['neutral-100'],
    },
  }),
  indicatorSeparator: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: colors['neutral-200'],
    marginTop: 8,
    marginBottom: 8,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    borderRadius: 8,
  }),
  // Pad the list so the per-row hover sits inset from the menu's rounded edge
  // (matches the contact-importer dropdowns) instead of full-width square corners.
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    padding: 6,
  }),
  // "No options" / empty-state message — 12px to match the field text.
  noOptionsMessage: (base: Record<string, unknown>) => ({
    ...base,
    fontSize: 12,
  }),
  // DS2026 menu items: neutral (gray-100) hover/selected fill — the same gray
  // hover used across the contact importer, sequencing, and homepage dropdowns.
  // Overrides react-select's default (and any green theme primary25).
  option: (
    base: Record<string, unknown>,
    state: { isFocused: boolean; isSelected: boolean }
  ) => ({
    ...base,
    cursor: 'pointer',
    fontSize: 12,
    borderRadius: 6, // rounded inset hover row
    color:
      state.isFocused || state.isSelected
        ? colors['neutral-900']
        : colors['neutral-700'],
    backgroundColor:
      state.isFocused || state.isSelected
        ? colors['neutral-100']
        : 'transparent',
    '&:active': { backgroundColor: colors['neutral-100'] },
  }),
}

// ---------------------------------------------------------------------------
// Shared control styling (exported so DS2026-restyled selects/phone inputs can
// match the text inputs exactly)
// ---------------------------------------------------------------------------

export type DsFieldSize = 'regular' | 'large'
export type DsFieldStatus = 'default' | 'error' | 'success'

// Explicit px min-heights (not rem-based h-9/h-11): the app root font-size is
// 14px, so rem utilities scale down (min-h-9 → 31.5px). Pinning px keeps these
// in lockstep with the react-select dropdowns' absolute 36px.
const SIZE_CLASS: Record<DsFieldSize, string> = {
  regular: 'min-h-[36px] px-3.5 py-2',
  large: 'min-h-[44px] px-3.5 py-3',
}

// Border colors use `!` because a global unlayered `input/textarea` rule forces
// the off-gray line-form color; `!border-neutral-300` keeps these inputs on the
// same Tailwind gray-300 as the react-select dropdowns.
const STATUS_CLASS: Record<DsFieldStatus, string> = {
  default:
    '!border-neutral-300 focus:!border-brand-active focus-visible:!border-brand-active focus:ring-brand-active/20 focus-visible:ring-brand-active/20',
  error:
    '!border-error focus:!border-error focus-visible:!border-error focus:ring-error/20 focus-visible:ring-error/20',
  success:
    '!border-success focus:!border-success focus-visible:!border-success focus:ring-success/20 focus-visible:ring-success/20',
}

/**
 * The DS2026 control look (border, radius, elevation, focus ring) as a single
 * className. Use for the bare `<input>`/`<textarea>` here, or to skin the
 * outer box of a react-select / phone-input wrapper so every field matches.
 */
export function dsControlClass(
  status: DsFieldStatus = 'default',
  size: DsFieldSize = 'regular',
  className?: string
): string {
  // Root font-size is 14px, so rem-based text-sm renders ~12px here (matches
  // the react-select dropdowns' explicit 12px). text-xs would be ~10.5px.
  return cn(
    'w-full rounded-lg border bg-surface text-sm text-fg tracking-[-0.2px]',
    'placeholder:text-neutral-400',
    // No resting shadow — only on hover (focus shows the ring instead).
    'hover:shadow-sm outline-none transition focus:ring-4 focus-visible:ring-4',
    'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-muted',
    SIZE_CLASS[size],
    STATUS_CLASS[status],
    className
  )
}

// ---------------------------------------------------------------------------
// DS2026 modal kit — single source of truth for the modal-level look.
//
// To build (or redesign) a modal in the DS2026 style, assemble these pieces:
//
//   <Modal
//     contentClassName={DS2026_MODAL_CONTENT_CLASS}   // roomier padding + radius
//     /* ...wrap buttons via <Ds2026ButtonProvider> or a ds2026-aware Modal */
//   >
//     <FieldGroup className={DS2026_MODAL_BODY_CLASS}> // section rhythm + insets
//       <DsTextField    control={...} name="..." label="..." placeholder="ex." />
//       <DsTextAreaField control={...} name="notes" label="Notes" optional />
//       {/* react-select fields: pass `ds2026` and/or styles={ds2026SelectStyles} */}
//       <CreatableSelect ds2026 styles={ds2026SelectStyles} ... />
//       <CreatableTagSelector ds2026 ... />
//       <CompanyAutocomplete ds2026 ... />
//       <PhoneInput ds2026 ... />
//     </FieldGroup>
//   </Modal>
//
// Change a value here → every DS2026 modal that consumes these updates with it.
// The reference implementation is the Add Contact modal (ContactModal +
// ContactDetailsStep, gated behind their `ds2026` prop). Buttons get the DS2026
// look from `Ds2026ButtonProvider` (../FbUI/Ds2026ButtonContext).
// ---------------------------------------------------------------------------

/** `<Modal contentClassName>` — DS2026 shell: extra padding + rounded-2xl. */
export const DS2026_MODAL_CONTENT_CLASS = 'px-10 py-6 sm:rounded-2xl'

/**
 * `<FieldGroup className>` for a DS2026 modal body:
 *  - gap-4: section rhythm (a touch tighter than the default gap-5)
 *  - px-1.5/pt-1: inset so the 4px focus ring clears the modal scroll
 *    container's `overflow-x: hidden` edge
 *  - pb-3: keeps the last field clear of the footer buttons
 */
export const DS2026_MODAL_BODY_CLASS = 'gap-4 px-1.5 pt-1 pb-3'

/** `<Modal dialogFooterClassName>` — DS2026 footer with a 16px gap between the
 *  Cancel / accept buttons (default is only ~7px). `sm:space-x-0` cancels the
 *  shared DialogFooter base's `sm:space-x-2` (twMerge treats space-x and gap as
 *  separate properties, so without this they'd stack to ~23px). */
export const DS2026_MODAL_FOOTER_CLASS =
  'flex flex-col-reverse gap-[16px] sm:flex-row sm:justify-end sm:space-x-0 py-2'

/** Pass to the footer buttons' `*ButtonProps.className` — pins the DS2026 modal
 *  CTA height to 44px (the Large size). Overrides both FbButton lg's `min-h-14`
 *  (49px) and its height, and keeps `flex-1` (acceptButtonProps.className
 *  replaces the Modal's own `flex-1`) so the buttons stay full-width. */
export const DS2026_MODAL_BUTTON_CLASS = 'flex-1 !h-[44px] !min-h-[44px]'

// ---------------------------------------------------------------------------
// Text content pieces
// ---------------------------------------------------------------------------

/** Vertical stack of fields with the DS2026 inter-field rhythm. */
export function FieldGroup({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={cn('flex flex-col gap-5', className)}>{children}</div>
}

export function FieldLabel({
  htmlFor,
  optional,
  className,
  children,
}: {
  htmlFor?: string
  optional?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    // Association is provided by callers via `htmlFor`/control id (text fields)
    // or by adjacent grouping (selects); the rule can't see that statically.
    // eslint-disable-next-line jsx-a11y/label-has-for
    <label
      htmlFor={htmlFor}
      className={cn(
        'text-sm font-medium tracking-[-0.2px] text-neutral-700 select-none',
        className
      )}
    >
      {children}
      {optional && (
        <span className="ml-1 font-normal text-fg-secondary">(optional)</span>
      )}
    </label>
  )
}

export function FieldDescription({
  id,
  className,
  children,
}: {
  id?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <p
      id={id}
      className={cn('text-xs tracking-[-0.2px] text-fg-secondary', className)}
    >
      {children}
    </p>
  )
}

export function FieldError({
  id,
  className,
  children,
}: {
  id?: string
  className?: string
  children?: React.ReactNode
}) {
  if (!children) return null
  return (
    <p
      id={id}
      className={cn(
        'text-xs font-medium tracking-[-0.2px] text-error-strong',
        className
      )}
    >
      {children}
    </p>
  )
}

/**
 * Small, input-scoped amber warning (non-blocking) — the DS2026 counterpart to
 * FieldError, e.g. "Missing domain…". AA-tuned `warning-strong`, not the bright
 * `warning` fill. Text-only (no icon), matching FieldError.
 */
export function FieldWarning({
  className,
  children,
}: {
  className?: string
  children?: React.ReactNode
}) {
  if (!children) return null
  return (
    <p
      className={cn(
        'text-xs font-medium tracking-[-0.2px] text-warning-strong',
        className
      )}
    >
      {children}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Bare controls
// ---------------------------------------------------------------------------

export const DsInput = React.forwardRef<
  HTMLInputElement,
  // Omit the native numeric `size` attribute so our DS size enum can use the name.
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    status?: DsFieldStatus
    fieldSize?: DsFieldSize
  }
>(({ className, status = 'default', fieldSize = 'regular', ...props }, ref) => (
  <input
    ref={ref}
    className={dsControlClass(status, fieldSize, className)}
    {...props}
  />
))
DsInput.displayName = 'DsInput'
DsInput.propTypes = {
  className: PropTypes.string,
  status: PropTypes.oneOf<DsFieldStatus>(['default', 'error', 'success']),
  fieldSize: PropTypes.oneOf<DsFieldSize>(['regular', 'large']),
}

export const DsTextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    status?: DsFieldStatus
  }
>(({ className, status = 'default', rows = 3, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={dsControlClass(
      status,
      'regular',
      cn('resize-y py-2.5', className)
    )}
    {...props}
  />
))
DsTextArea.displayName = 'DsTextArea'
DsTextArea.propTypes = {
  className: PropTypes.string,
  status: PropTypes.oneOf<DsFieldStatus>(['default', 'error', 'success']),
  rows: PropTypes.number,
}

// ---------------------------------------------------------------------------
// react-hook-form wired fields
// ---------------------------------------------------------------------------

interface DsFieldBaseProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  optional?: boolean
  disabled?: boolean
  /** Helper text under the field (hidden while an error shows). */
  description?: string
  className?: string
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  onBlur?: () => void
}

function useFieldA11y(
  name: string,
  hasDescription: boolean,
  hasError: boolean
) {
  const descriptionId = `${name}-description`
  const errorId = `${name}-error`
  // Plain space-join (not cn) — these are aria-describedby IDs, not class names.
  const describedBy =
    [hasDescription && descriptionId, hasError && errorId]
      .filter(Boolean)
      .join(' ') || undefined
  return { descriptionId, errorId, describedBy }
}

export function DsTextField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  optional,
  disabled,
  description,
  className,
  rules,
  onBlur,
  type = 'text',
  fieldSize = 'regular',
  autoComplete,
  inputClassName,
}: DsFieldBaseProps<T> & {
  type?: string
  fieldSize?: DsFieldSize
  autoComplete?: string
  /** Extra classes on the `<input>` itself (e.g. `tabular-nums`). */
  inputClassName?: string
}) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules })
  const { descriptionId, errorId, describedBy } = useFieldA11y(
    name,
    !!description,
    !!error
  )

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label && (
        <FieldLabel htmlFor={name} optional={optional}>
          {label}
        </FieldLabel>
      )}
      <DsInput
        id={name}
        type={type}
        fieldSize={fieldSize}
        status={error ? 'error' : 'default'}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className={inputClassName}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        value={field.value ?? ''}
        name={field.name}
        ref={field.ref}
        onChange={field.onChange}
        onBlur={() => {
          field.onBlur()
          onBlur?.()
        }}
      />
      {description && !error && (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      )}
      <FieldError id={errorId}>{error?.message}</FieldError>
    </div>
  )
}

export function DsTextAreaField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  optional,
  disabled,
  description,
  className,
  rules,
  onBlur,
  rows = 3,
}: DsFieldBaseProps<T> & { rows?: number }) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules })
  const { descriptionId, errorId, describedBy } = useFieldA11y(
    name,
    !!description,
    !!error
  )

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label && (
        <FieldLabel htmlFor={name} optional={optional}>
          {label}
        </FieldLabel>
      )}
      <DsTextArea
        id={name}
        rows={rows}
        status={error ? 'error' : 'default'}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        value={field.value ?? ''}
        name={field.name}
        ref={field.ref}
        onChange={field.onChange}
        onBlur={() => {
          field.onBlur()
          onBlur?.()
        }}
      />
      {description && !error && (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      )}
      <FieldError id={errorId}>{error?.message}</FieldError>
    </div>
  )
}
